"use client";

/**
 * AlertTopBarPublic — right-hand cluster of the header for NON-OWNER
 * visitors to /alerts/{id}.
 *
 * Layout (right-to-left):
 *   [Subscribed ✓ ▾]  or  [Subscribe]     ← email opt-in (orthogonal to view)
 *   [DELAYED (5d) | LIVE]                 ← segmented toggle
 *
 * Behaviour:
 *   • Toggle       → paid users flip freely between LIVE / DELAYED, no modal.
 *                    Free users clicking LIVE → paywall (auth or pricing).
 *   • Subscribe    → paid: 1-click API call, button flips to Subscribed ✓.
 *                    free: pricing modal.
 *   • Paused alert → chip swaps for a non-clickable "Paused" badge and the
 *                    Subscribe button disables. Truth wins, regardless of
 *                    subscription state.
 *
 * Why this component takes no paywall props:
 *   Everything comes from `useAlertPublicView()` at the page level. No
 *   prop-drilling through Chat → Artifact → this.
 */
import { motion } from "framer-motion";
import {
  Radio,
  Lock,
  PauseCircle,
  CheckCircle2,
  Loader2,
  Mail,
  X,
  Clock,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type AlertDoc } from "@/lib/api-client";
import { useAlertPublicView } from "./AlertPaywallContext";

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background";

interface AlertTopBarPublicProps {
  alert: AlertDoc;
  /** Subscriber's email, shown in the "Subscribed ✓" dropdown. Comes
   *  from `GET /alerts/{id}`, not the context — we don't refetch it. */
  subscribedEmail: string | null;
}

export function AlertTopBarPublic({
  alert,
  subscribedEmail,
}: AlertTopBarPublicProps) {
  const {
    view,
    canGoLive,
    setView,
    delayedAgeLabel,
    isSubscribed,
    subscribeBusy,
    subscribe,
    unsubscribe,
  } = useAlertPublicView();

  // Failed/errored alerts are not running: show the paused badge and block
  // subscribing, same as paused (a dead alert must never advertise as live).
  const isPaused =
    alert.status === "paused" ||
    alert.status === "test_failed" ||
    alert.status === "errored";
  const isSignal = alert.kind === "signal";

  return (
    <div className="flex items-center gap-2 shrink-0 min-w-max whitespace-nowrap">
      {isPaused ? (
        <PausedBadge />
      ) : (
        <ViewToggle
          view={view}
          canGoLive={canGoLive}
          delayedAgeLabel={delayedAgeLabel}
          onChange={setView}
          isSignal={isSignal}
        />
      )}

      {isSubscribed ? (
        <SubscribedButton
          email={subscribedEmail}
          busy={subscribeBusy}
          onUnsubscribe={unsubscribe}
        />
      ) : (
        <SubscribeButton
          busy={subscribeBusy}
          disabled={isPaused}
          onClick={subscribe}
          isSignal={isSignal}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Segmented toggle
// ─────────────────────────────────────────────────────────────────────────────

function ViewToggle({
  view,
  canGoLive,
  delayedAgeLabel,
  onChange,
  isSignal = false,
}: {
  view: "live" | "delayed";
  canGoLive: boolean;
  delayedAgeLabel: string | null;
  onChange: (v: "live" | "delayed") => void;
  isSignal?: boolean;
}) {
  const isLive = view === "live";

  return (
    <div
      data-tour="alert-public-view-toggle"
      role="radiogroup"
      aria-label="View mode"
      className="inline-flex items-center h-8 p-0.5 rounded-full border border-slate-600/60 bg-slate-900/60 shrink-0"
    >
      <SegmentButton
        role="radio"
        selected={!isLive}
        onClick={() => onChange("delayed")}
        title="Show the stale first-ever snapshot"
        className="rounded-full"
        isSignal={isSignal}
      >
        <Clock className="w-3 h-3" strokeWidth={2.4} />
        <span>Delayed</span>
        {delayedAgeLabel && (
          <span className="text-[9.5px] font-medium opacity-70 tabular-nums ml-0.5">
            ({delayedAgeLabel})
          </span>
        )}
      </SegmentButton>

      <SegmentButton
        role="radio"
        selected={isLive}
        onClick={() => onChange("live")}
        title={
          canGoLive
            ? "See the freshest dashboard"
            : "Upgrade to any paid plan to see live"
        }
        className="rounded-full"
        isSignal={isSignal}
      >
        {canGoLive ? (
          <Radio className="w-3 h-3" strokeWidth={2.4} />
        ) : (
          <Lock className="w-3 h-3" strokeWidth={2.4} />
        )}
        <span>Live</span>
      </SegmentButton>
    </div>
  );
}

function SegmentButton({
  role,
  selected,
  onClick,
  title,
  children,
  className = "",
  isSignal = false,
}: {
  role: string;
  selected: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
  isSignal?: boolean;
}) {
  return (
    <motion.button
      role={role}
      aria-checked={selected}
      type="button"
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      title={title}
      className={[
        "inline-flex items-center gap-1 h-7 px-3 text-[10.5px] font-semibold uppercase tracking-wider transition-colors shrink-0 whitespace-nowrap",
        selected
          ? isSignal
            ? "bg-emerald-500/90 text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset]"
            : "bg-sky-500/90 text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset]"
          : "text-slate-300 hover:text-slate-100 hover:bg-slate-800/60",
        FOCUS_RING,
        className,
      ].join(" ")}
    >
      {children}
    </motion.button>
  );
}

function PausedBadge() {
  return (
    <div
      title="The owner paused this alert"
      className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-200 text-[10.5px] font-semibold uppercase tracking-wider cursor-default"
    >
      <PauseCircle className="w-3 h-3" strokeWidth={2.4} />
      <span>Paused</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subscribe button states
// ─────────────────────────────────────────────────────────────────────────────

function SubscribeButton({
  busy,
  disabled,
  onClick,
  isSignal = false,
}: {
  busy: boolean;
  disabled?: boolean;
  onClick: () => void;
  isSignal?: boolean;
}) {
  return (
    <motion.button
      data-tour="alert-public-subscribe"
      type="button"
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      disabled={busy || disabled}
      title={
        disabled
          ? "Subscribing is disabled while the alert is paused"
          : "Subscribe to receive an email every time this alert fires"
      }
      className={[
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-md shrink-0 whitespace-nowrap",
        isSignal
          ? "bg-emerald-500 hover:bg-emerald-400 text-white"
          : "bg-sky-500 hover:bg-sky-400 text-white",
        "text-[12px] font-semibold transition-colors",
        "shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_1px_2px_0_rgba(0,0,0,0.3)]",
        isSignal
          ? "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-500"
          : "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-sky-500",
        FOCUS_RING,
      ].join(" ")}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Mail className="w-3.5 h-3.5" strokeWidth={2.2} />
      )}
      Subscribe
    </motion.button>
  );
}

function SubscribedButton({
  email,
  busy,
  onUnsubscribe,
}: {
  email: string | null;
  busy: boolean;
  onUnsubscribe: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-tour="alert-public-subscribe"
          type="button"
          title={email ? `Subscribed as ${email}` : "You're subscribed"}
          className={[
            "inline-flex items-center gap-1.5 h-8 px-3 rounded-md shrink-0 whitespace-nowrap",
            "bg-emerald-500/15 border border-emerald-400/40 text-emerald-200",
            "hover:bg-emerald-500/20 hover:border-emerald-400/55",
            "text-[12px] font-semibold transition-colors",
            FOCUS_RING,
          ].join(" ")}
        >
          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
          <span>Subscribed</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="w-[260px]">
        <div className="px-2 py-2 flex items-start gap-2 text-[11px] text-muted-foreground">
          <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-muted-foreground/80">
              Emails go to
            </div>
            <div className="text-foreground/90 text-[12px] truncate">
              {email ?? "your account email"}
            </div>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onUnsubscribe();
          }}
          disabled={busy}
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
          ) : (
            <X className="w-3.5 h-3.5 mr-2" />
          )}
          <span>{busy ? "Unsubscribing…" : "Unsubscribe"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
